import { getStoredMeta, loadStoredSession } from "@itaas/core/auth/auth.storage";

export const RH_API_BASE_URL =
  import.meta.env.VITE_RH_API_BASE_URL ?? "/api/rh";

const RH_API_DEV_PORT = 3060;

export class RhApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "RhApiError";
  }
}

interface RhApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

function tenantSlug(): string {
  return loadStoredSession()?.tenant?.slug ?? "demo";
}

async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Tenant-Slug": tenantSlug(),
  };
  const { accessToken } = getStoredMeta();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
}

export async function rhRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers = await buildHeaders();
  const response = await fetch(`${RH_API_BASE_URL}${path}`, {
    method,
    headers,
    body:
      method === "GET" || method === "DELETE"
        ? undefined
        : JSON.stringify(options.body ?? {}),
  }).catch(() => {
    throw new RhApiError(
      `API RH inaccessible — démarrez RH API (port ${RH_API_DEV_PORT}).`,
      0,
    );
  });

  const text = await response.text();
  if (!text.trim()) {
    throw new RhApiError(`Erreur API RH (${response.status})`, response.status);
  }

  const json = JSON.parse(text) as RhApiEnvelope<T> | { message?: string };
  if (!response.ok) {
    const message =
      "message" in json && json.message ? json.message : `Erreur API RH (${response.status})`;
    throw new RhApiError(message, response.status);
  }
  if (json && typeof json === "object" && "success" in json && "data" in json) {
    return (json as RhApiEnvelope<T>).data;
  }
  return json as T;
}
